import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { ArrowLeft, User, Mail, Phone, MapPin, Map, Trash2, Clock, CheckCircle, AlertCircle, Loader2, Send, MousePointerClick, ShoppingCart, Store, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusLabels = {
  in_corso: { label: 'In corso', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  nuovo: { label: 'Nuovo', color: 'bg-blue-100 text-blue-800', icon: Send },
  inviato: { label: 'Inviato', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  contattato: { label: 'Contattato', color: 'bg-purple-100 text-purple-800', icon: Phone },
  convertito: { label: 'Convertito', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  abbandonato: { label: 'Abbandonato', color: 'bg-gray-100 text-gray-600', icon: AlertCircle }
};

const routingLabels = {
  online_shop: { label: 'Online Shop', color: 'bg-purple-50 text-purple-700' },
  physical_dealer: { label: 'Rivenditore fisico', color: 'bg-orange-50 text-orange-700' },
  online_fallback: { label: 'Online (fallback)', color: 'bg-blue-50 text-blue-700' }
};

const InfoRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
      <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium text-gray-900 break-words">{value}</p>
      </div>
    </div>
  );
};

const AdminLeadDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('admin_token');
  const logout = () => { localStorage.removeItem('admin_token'); };

  const fetchLead = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/leads/${leadId}`, { headers: { Authorization: `Bearer ${token}` } });
      setLead(res.data);
    } catch (err) {
      if (err.response?.status === 401) { logout(); navigate('/admin6y5dv2x/login'); }
      else if (err.response?.status === 404) { setError('Lead non trovato'); }
      else { setError('Errore nel caricamento'); }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchLead(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const handleDelete = async () => {
    if (!window.confirm('Eliminare questo lead?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/leads/${leadId}`, { headers: { Authorization: `Bearer ${token}` } });
      navigate('/admin6y5dv2x/leads');
    } catch { alert('Eliminazione fallita'); }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-stocker-green" /></div>;
  if (error) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-center"><AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" /><p className="text-gray-600 mb-4">{error}</p><Button onClick={() => navigate('/admin6y5dv2x/leads')}>Torna alla lista</Button></div></div>;

  const StatusIcon = statusLabels[lead?.status]?.icon || Clock;
  const clicks = lead?.click_events || [];
  const dealersShown = lead?.dealers_shown || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/admin6y5dv2x/leads')} className="p-2"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="font-bold text-gray-900 text-sm">Dettaglio Lead</h1>
              <p className="text-[11px] text-gray-400 font-mono">{lead?.lead_id}</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleDelete} className="text-red-600 border-red-200 hover:bg-red-50 text-xs">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Column 1+2: Main info */}
          <div className="lg:col-span-2 space-y-5">

            {/* Status + Steps */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-sm text-gray-900">Status</h2>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${statusLabels[lead?.status]?.color}`}>
                  <StatusIcon className="w-3.5 h-3.5" />
                  {statusLabels[lead?.status]?.label || lead?.status}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                {[1, 2, 3].map(s => (
                  <div key={s} className={`p-2.5 rounded-lg text-xs ${lead?.step_max_raggiunto >= s ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-400'}`}>
                    <p className="font-bold text-sm">{lead?.step_max_raggiunto >= s ? 'OK' : '-'}</p>
                    <p>Step {s}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="font-bold text-sm text-gray-900 mb-3">Dati contatto</h2>
              <InfoRow icon={User} label="Nome" value={lead?.first_name} />
              <InfoRow icon={User} label="Cognome" value={lead?.last_name} />
              <InfoRow icon={Mail} label="Email" value={lead?.email} />
              <InfoRow icon={Phone} label="Telefono" value={lead?.phone} />
              <InfoRow icon={MapPin} label="Provincia" value={lead?.provincia} />
              <InfoRow icon={MapPin} label="Città" value={lead?.city} />
              <InfoRow icon={MapPin} label="CAP" value={lead?.postcode} />
            </div>

            {/* Consents (GDPR) */}
            <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="lead-consents-card">
              <h2 className="font-bold text-sm text-gray-900 mb-3">Consensi</h2>
              <div className="space-y-2.5">
                <div className="flex items-start gap-2.5">
                  {lead?.privacy_accepted ? (
                    <CheckCircle className="w-4 h-4 text-stocker-green mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      Privacy policy <span className={lead?.privacy_accepted ? 'text-stocker-green' : 'text-red-500'}>· {lead?.privacy_accepted ? 'Accettata' : 'Non accettata'}</span>
                    </p>
                    {lead?.privacy_accepted_at && (
                      <p className="text-[11px] text-gray-500">il {new Date(lead.privacy_accepted_at).toLocaleString('it-IT')}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  {lead?.consent_marketing ? (
                    <CheckCircle className="w-4 h-4 text-stocker-green mt-0.5 flex-shrink-0" />
                  ) : (
                    <span className="w-4 h-4 rounded-full border-2 border-gray-300 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      Newsletter marketing <span className={lead?.consent_marketing ? 'text-stocker-green' : 'text-gray-500'}>· {lead?.consent_marketing ? 'Sì' : 'No'}</span>
                    </p>
                    {lead?.consent_marketing && lead?.consent_marketing_at && (
                      <p className="text-[11px] text-gray-500">il {new Date(lead.consent_marketing_at).toLocaleString('it-IT')}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Garden */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="font-bold text-sm text-gray-900 mb-3">Giardino</h2>
              <InfoRow icon={Map} label="Modalità" value={lead?.input_mode === 'mappa' ? 'Mappa' : 'Manuale'} />
              <InfoRow icon={Map} label="Superficie" value={lead?.area_m2 ? `~${lead.area_m2} m²` : null} />
              <InfoRow icon={Map} label="Perimetro" value={lead?.perimeter_m ? `~${lead.perimeter_m} m` : null} />
              {lead?.pin_lat && lead?.pin_lng && (
                <InfoRow icon={MapPin} label="Coordinate" value={`${lead.pin_lat.toFixed(5)}, ${lead.pin_lng.toFixed(5)}`} />
              )}
            </div>

            {/* Kit Selection */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="font-bold text-sm text-gray-900 mb-3">Kit</h2>
              {lead?.kit_selected_id ? (
                <div className="bg-stocker-green/5 border border-stocker-green/20 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-gray-900">{lead.kit_selected_name}</span>
                    {lead.kit_consigliato && (
                      <span className="px-2 py-0.5 bg-stocker-green text-white text-[10px] rounded-full font-medium">CONSIGLIATO</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500 text-xs">Copertura:</span><span className="ml-1 font-medium">{lead.kit_selected_coverage?.toLocaleString()} m²</span></div>
                    <div><span className="text-gray-500 text-xs">Prezzo:</span><span className="ml-1 font-medium text-red-600">€ {lead.kit_selected_price?.toLocaleString()}</span></div>
                  </div>
                  {!lead.kit_consigliato && (
                    <p className="mt-2 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1">Il cliente ha scelto un kit diverso dal consigliato</p>
                  )}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">Nessun kit selezionato</p>
              )}
            </div>

            {/* Routing + Dealers shown */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="font-bold text-sm text-gray-900 mb-3">Routing & Rivenditori</h2>
              {lead?.routing_type && (
                <div className="mb-3">
                  <span className={`px-2.5 py-1 rounded text-xs font-medium ${routingLabels[lead.routing_type]?.color || 'bg-gray-100'}`}>
                    {routingLabels[lead.routing_type]?.label || lead.routing_type}
                  </span>
                  {lead.assigned_dealer_name && (
                    <span className="ml-2 text-sm text-gray-700">→ <strong>{lead.assigned_dealer_name}</strong></span>
                  )}
                </div>
              )}
              {dealersShown.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 font-medium uppercase">Rivenditori mostrati al cliente:</p>
                  {dealersShown.map((d, i) => (
                    <div key={`dealer-shown-${d.dealer_id || d.shop_id || i}`} className="flex items-center gap-2 text-sm py-1.5 px-3 bg-gray-50 rounded-lg">
                      {d.type === 'physical_dealer' ? <Store className="w-4 h-4 text-orange-500" /> : <ShoppingCart className="w-4 h-4 text-purple-500" />}
                      <span className="font-medium text-gray-800">{d.name}</span>
                      <span className="text-[10px] text-gray-400 ml-auto">{d.type === 'physical_dealer' ? 'Fisico' : 'Online'}</span>
                    </div>
                  ))}
                </div>
              )}
              {!lead?.routing_type && <p className="text-gray-400 text-sm">Non ancora assegnato</p>}
            </div>

            {/* Click Tracking */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2">
                <MousePointerClick className="w-4 h-4" /> Click Tracking
              </h2>
              {clicks.length > 0 ? (
                <div className="space-y-2">
                  {clicks.map((c, i) => (
                    <div key={`click-${c.timestamp || i}`} className="flex items-center gap-3 text-sm py-2 px-3 bg-gray-50 rounded-lg">
                      <ExternalLink className="w-3.5 h-3.5 text-stocker-green flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800">{c.target_name}</p>
                        <p className="text-[10px] text-gray-400 truncate">{c.url}</p>
                      </div>
                      <span className="text-[10px] text-gray-400 flex-shrink-0">
                        {c.timestamp ? new Date(c.timestamp).toLocaleString('it-IT') : ''}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm text-center py-3">Nessun click registrato</p>
              )}
            </div>
          </div>

          {/* Column 3: Email Status + Timeline */}
          <div className="lg:col-span-1 space-y-5">
            {/* Email CRM */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email Status
              </h2>
              <div className="space-y-2.5">
                <div className={`flex items-center gap-2 p-2.5 rounded-lg text-xs ${lead?.email_to_user_sent ? 'bg-green-50' : 'bg-gray-50'}`}>
                  <div className={`w-2 h-2 rounded-full ${lead?.email_to_user_sent ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">Email al cliente</p>
                    <p className="text-gray-500">Kit + rivenditori</p>
                  </div>
                  <span className="font-medium">{lead?.email_to_user_sent ? 'Inviata' : 'Non inviata'}</span>
                </div>
                <div className={`flex items-center gap-2 p-2.5 rounded-lg text-xs ${lead?.email_to_dealer_sent ? 'bg-green-50' : 'bg-gray-50'}`}>
                  <div className={`w-2 h-2 rounded-full ${lead?.email_to_dealer_sent ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">Email al rivenditore</p>
                    <p className="text-gray-500">Dati lead + kit</p>
                  </div>
                  <span className="font-medium">{lead?.email_to_dealer_sent ? 'Inviata' : 'Non inviata'}</span>
                </div>
                <div className={`flex items-center gap-2 p-2.5 rounded-lg text-xs ${lead?.email_to_stocker_sent ? 'bg-green-50' : 'bg-gray-50'}`}>
                  <div className={`w-2 h-2 rounded-full ${lead?.email_to_stocker_sent ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">Email a Stocker</p>
                    <p className="text-gray-500">Nuovo lead</p>
                  </div>
                  <span className="font-medium">{lead?.email_to_stocker_sent ? 'Inviata' : 'Non inviata'}</span>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 sticky top-20">
              <h2 className="font-bold text-sm text-gray-900 mb-3">Timeline</h2>
              <div className="mb-3 p-2.5 bg-gray-50 rounded-lg text-xs">
                <div className="flex justify-between"><span className="text-gray-500">Creato</span><span className="font-medium">{lead?.created_at ? new Date(lead.created_at).toLocaleString('it-IT') : '-'}</span></div>
                {lead?.sent_at && <div className="flex justify-between mt-1"><span className="text-gray-500">Inviato</span><span className="font-medium">{new Date(lead.sent_at).toLocaleString('it-IT')}</span></div>}
              </div>
              {lead?.events?.length > 0 ? (
                <div className="space-y-3">
                  {lead.events.map((event, idx) => (
                    <div key={event.event_id || idx} className="flex gap-2.5">
                      <div className="flex flex-col items-center">
                        <div className="w-2.5 h-2.5 rounded-full bg-stocker-green mt-0.5" />
                        {idx < lead.events.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-0.5" />}
                      </div>
                      <div className="flex-1 pb-3">
                        <p className="text-xs font-medium text-gray-800">{event.event_type}</p>
                        <p className="text-[10px] text-gray-400">Step {event.step_number} · {new Date(event.timestamp).toLocaleString('it-IT')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-xs text-center py-4">Nessun evento</p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminLeadDetail;
